import { SVGIcon } from '@/components_deprecated'
import type { DriverActivityReport } from '@/modules/activities/types.graphql'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import type { ActivitiesModuleConfig } from '@/modules/companies/types.graphql'
import { format } from '@/utils/date-fns'
import { useModuleConfig } from '@/utils/hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { enforceHourMinuteString } from '../../util'
import { AllActivitiesSumTable } from './AllActivitiesSumTable'
import { Icon } from '@ur-solutions/react-components'
import {
  MonthSummaryCards,
  type MonthSummaryCard,
} from '@/components/month-summary-cards'

interface ActivitiesMonthSummariesProps {
  month: Date
  showOvertime?: boolean
  useWorkDayReport?: boolean
  report: DriverActivityReport
}

/**
 * Activities-view adapter: maps a `DriverActivityReport` plus company
 * module flags onto the shared `MonthSummaryCards` primitive. Renders
 * `AllActivitiesSumTable` below when the salary module is inactive
 * (legacy behavior).
 */
export const ActivitiesMonthSummaries: React.JSX.CFC<
  ActivitiesMonthSummariesProps
> = ({ month, showOvertime, useWorkDayReport = false, report }) => {
  const translations = useTranslate({
    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    pause: 'common.pause',
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'activities.sum-overtime',
    available: 'activities.availability-time',
    plusNNextMonth: ['activities.plus-n-next-month', { n: '' }],
  })
  const salaryModuleActive = useModuleConfig(Module.SALARY).moduleActive

  const summaryOptions = {
    pause: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_PAUSE,
    availability: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_AVAILABILITY_TIME,
    driving: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_DRIVING,
    otherWork: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_OTHER_WORK,
    overtime: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_TOTAL_OVERTIME,
  }

  const { getModuleOptionStatus } = useModuleConfig<ActivitiesModuleConfig>(
    Module.ACTIVITIES
  )

  const summaryStatus = Object.fromEntries(
    Object.entries(summaryOptions).map(([key, value]) => [
      key,
      getModuleOptionStatus(value),
    ])
  ) as Record<
    keyof typeof summaryOptions,
    ReturnType<typeof getModuleOptionStatus>
  >

  const overtime = report?.overtime
  const showOvertimeCard = !!(showOvertime && summaryStatus.overtime)
  const showNextMonthHint =
    (useWorkDayReport || !!report?.salaryWorkTime?.useWeekOvertime) &&
    report?.overtimeNextMonth !== '00:00'

  const cards: MonthSummaryCard[] = []
  if (summaryStatus.driving) {
    cards.push({
      key: 'driving',
      label: translations.driving,
      value: enforceHourMinuteString(report?.timeDriving) ?? '-',
      iconBg: '#399D26',
      icon: <SVGIcon icon="truck" />,
    })
  }
  if (summaryStatus.otherWork) {
    cards.push({
      key: 'otherWork',
      label: translations.otherWork,
      value: enforceHourMinuteString(report?.timeOtherWork) ?? '-',
      iconBg: '#ffb600',
      icon: <Icon icon="briefcase" fixedWidth size="2.92em" />,
    })
  }
  if (summaryStatus.availability) {
    cards.push({
      key: 'availability',
      label: translations.available,
      value: enforceHourMinuteString(report?.timeAvailability) ?? '-',
      iconBg: '#00c7b1',
      icon: (
        <Icon
          icon="hand-holding"
          fixedWidth
          translateY="-0.8rem"
          size="2.622em"
        />
      ),
    })
  }
  if (summaryStatus.pause) {
    cards.push({
      key: 'pause',
      label: translations.pause,
      value: enforceHourMinuteString(report?.timePause) ?? '-',
      iconBg: '#4C9CF1',
      icon: <Icon icon="pause" fixedWidth size="2.92em" />,
    })
  }
  if (showOvertimeCard) {
    cards.push({
      key: 'overtime',
      label: translations.overtime,
      value: enforceHourMinuteString(overtime) ?? '-',
      iconBg: '#fabbce',
      icon: <SVGIcon icon="clock" />,
      hint: showNextMonthHint
        ? translations.plusNNextMonth({
            n: enforceHourMinuteString(report?.overtimeNextMonth),
          })
        : undefined,
    })
  }

  return (
    <>
      <MonthSummaryCards
        title={translations.sumForMonth({ month: format(month, 'MMMM') })}
        cards={cards}
      />
      {!salaryModuleActive && <AllActivitiesSumTable report={report} />}
    </>
  )
}
